import { SupabaseClient } from '@supabase/supabase-js';
import { getAllTags } from './tagService';

export interface TagValidationResult {
  existingTags: string[];
  newTags: string[];
  hasNewTags: boolean;
}

/**
 * Normalizes a tag by trimming whitespace and converting to lowercase for comparison
 */
export function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase();
}

/**
 * Normalizes an array of tags and removes duplicates (case-insensitive)
 */
export function normalizeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  return tags
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0)
    .filter(tag => {
      const normalized = normalizeTag(tag);
      if (seen.has(normalized)) {
        return false;
      }
      seen.add(normalized);
      return true;
    });
}

/**
 * Checks which tags from the provided list are new (don't exist in database)
 * and which ones already exist
 */
export async function checkNewTags(
  supabase: SupabaseClient, 
  tagsToCheck: string[]
): Promise<TagValidationResult> {
  try {
    // Normalize input tags
    const normalizedInputTags = normalizeTags(tagsToCheck);
    
    if (normalizedInputTags.length === 0) {
      return {
        existingTags: [],
        newTags: [],
        hasNewTags: false
      };
    }

    // Get all existing tags from database
    const existingTagsFromDb = await getAllTags(supabase);
    
    // Normalize existing tags for comparison
    const normalizedExistingTags = existingTagsFromDb.map(normalizeTag);
    
    // Categorize input tags as existing or new
    const existingTags: string[] = [];
    const newTags: string[] = [];
    
    for (const inputTag of normalizedInputTags) {
      const normalizedInputTag = normalizeTag(inputTag);
      
      if (normalizedExistingTags.includes(normalizedInputTag)) {
        existingTags.push(inputTag);
      } else {
        newTags.push(inputTag);
      }
    }
    
    return {
      existingTags: existingTags.sort(),
      newTags: newTags.sort(),
      hasNewTags: newTags.length > 0
    };
    
  } catch (error) {
    console.error('Error validating tags:', error);
    throw new Error('Failed to validate tags against database');
  }
} 